import {
  auth,
  db,
  collection,
  doc,
  addDoc,
  setDoc,
  getDoc,
  query,
  where,
  getDocs,
  orderBy,
  onSnapshot,
  serverTimestamp,
  onAuthStateChanged,
  storage,
  storageRef,
  uploadBytes,
  getDownloadURL,
} from "@/firebase";
import type { FlashcardResult, Flashcard } from "@/services/geminiService";
import {
  logDebug,
  logInfo,
  logWarn,
  logError,
  logSuccess,
  logSecurity,
} from "@/utils/secureLogger";

export interface FlashcardRecord {
  flashcards_id?: string;
  session_id: string;
  user_id: string;
  userName: string;
  flashcard_data: Flashcard[]; // Array of front/back card pairs
  date_generated: any; // Firestore timestamp
  ai_model_used: string;
  source_type: "pdf" | "txt" | "docx" | "note";
  input_length: number;
  subject: string;
  topic: string;
  sources: string[]; // Credible sources used for content generation
  filename?: string; // Original filename/source name
  student_notes?: string; // Original student notes/content
  student_notes_url?: string; // Firebase Storage URL for uploaded files
}

class FlashcardService {
  private flashcardsCollection = collection(db, "flashcards");

  /**
   * Upload student notes file to Firebase Storage
   */
  private async uploadStudentNotesToStorage(file: File, userId: string): Promise<string> {
    try {
      logInfo("Starting file upload to Firebase Storage", {
        fileName: file.name,
        fileSize: file.size,
      });

      // Create unique filename with timestamp
      const timestamp = Date.now();
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const storagePath = `student_notes/${userId}/${timestamp}_${sanitizedFileName}`;

      // Create storage reference
      const fileRef = storageRef(storage, storagePath);

      // Upload file
      logDebug("Uploading file to Storage", { path: storagePath });
      const uploadResult = await uploadBytes(fileRef, file);
      logSuccess("File uploaded successfully", { path: storagePath });

      // Get download URL
      const downloadURL = await getDownloadURL(uploadResult.ref);
      logSuccess("Download URL retrieved", { url: downloadURL.substring(0, 50) + "..." });

      return downloadURL;
    } catch (error) {
      logError("Error uploading file to Storage", error);
      throw new Error(
        `Failed to upload file: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  }

  /**
   * Create a basic user document for development/testing purposes
   */
  private async createBasicUserDocument(userId: string, email: string): Promise<void> {
    try {
      logDebug("Creating basic user document for development purposes");

      const userData = {
        uid: userId,
        firstName: "Test",
        lastName: "Student",
        email: email,
        userType: "student",
        isEmailVerified: true,
        isVerified: true,
        registrationCompleted: true,
        createdAt: new Date(),
        updatedAt: new Date(),
        subjects: ["Mathematics", "Science", "English"],
        preferences: {
          language: "english",
          notifications: true,
        },
      };

      await setDoc(doc(db, "users", userId), userData);
      logSuccess("Basic user document created successfully");
    } catch (error) {
      logError("Error creating basic user document", error);
      throw error;
    }
  }

  /**
   * Get current authenticated student user information
   * @throws {Error} If user is not authenticated or not a student
   */
  private async getCurrentStudentUser(): Promise<{ userId: string; userName: string }> {
    try {
      logDebug("Starting user authentication validation");
      const currentUser = auth.currentUser;
      if (!currentUser) {
        logError("No authenticated user found");
        throw new Error("No authenticated user found - please log in again");
      }

      logSecurity("User authentication verified", "getCurrentStudentUser");
      logDebug("Fetching user document from database");

      // Get user data from Firestore
      const userDocRef = doc(db, "users", currentUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        logWarn("User document not found, creating basic user document");
        try {
          await this.createBasicUserDocument(currentUser.uid, currentUser.email || "");
          // Return basic user info after creating document
          return {
            userId: currentUser.uid,
            userName: "Test Student",
          };
        } catch (error) {
          logError("Failed to create user document", error);
          throw new Error(
            "User account not properly set up. Please contact support or try logging in again."
          );
        }
      }

      const userData = userDoc.data();
      logDebug("User data retrieved", { userType: userData.userType });

      // Verify user is a student or validator
      if (userData.userType !== "student" && userData.userType !== "validator") {
        logWarn("Non-student/validator user attempting to save flashcards", {
          userType: userData.userType,
        });
        throw new Error("Only students and validators can save flashcards to their library.");
      }

      // Construct user name
      const firstName = userData.firstName || "";
      const lastName = userData.lastName || "";
      const userName = `${firstName} ${lastName}`.trim() || "Unknown User";

      logSuccess("User validated successfully");

      return {
        userId: currentUser.uid,
        userName,
      };
    } catch (error) {
      logError("❌ CRITICAL ERROR: Unable to get current student user", error);
      console.error("🚨 User authentication error details:", error);
      // ✅ FIX: Throw error instead of returning null to propagate to caller
      throw new Error(
        `User authentication failed: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  }

  /**
   * Get active subjects from Firebase subjects collection
   */
  private async getActiveSubjects(): Promise<string[]> {
    try {
      const subjectsQuery = query(collection(db, "subjects"), where("status", "==", "active"));

      const subjectsSnapshot = await getDocs(subjectsQuery);
      const subjects: string[] = [];

      subjectsSnapshot.forEach((doc) => {
        const subjectData = doc.data();
        if (subjectData.name) {
          subjects.push(subjectData.name);
        }
      });

      return subjects;
    } catch (error) {
      logError("Error fetching active subjects", error);
      return [];
    }
  }

  /**
   * Find the best matching subject from active subjects based on AI analysis
   */
  private findBestMatchingSubject(aiSubject: string, activeSubjects: string[]): string {
    if (!aiSubject || activeSubjects.length === 0) {
      return "General"; // Default fallback
    }

    const aiSubjectLower = aiSubject.toLowerCase();

    // First, try exact match
    for (const subject of activeSubjects) {
      if (subject.toLowerCase() === aiSubjectLower) {
        return subject;
      }
    }

    // Then try partial matches
    for (const subject of activeSubjects) {
      const subjectLower = subject.toLowerCase();
      if (aiSubjectLower.includes(subjectLower) || subjectLower.includes(aiSubjectLower)) {
        return subject;
      }
    }

    // Try common subject mappings
    const subjectMappings: Record<string, string[]> = {
      Mathematics: ["math", "algebra", "geometry", "calculus", "statistics", "trigonometry"],
      Science: ["biology", "chemistry", "physics", "earth science", "environmental science"],
      English: ["literature", "writing", "grammar", "reading", "english language"],
      History: ["world history", "american history", "social studies", "historical"],
      "Computer Science": ["programming", "coding", "computer", "software", "technology"],
      Arts: ["art", "music", "drama", "theater", "visual arts", "performing arts"],
      Language: ["spanish", "french", "german", "chinese", "japanese", "foreign language"],
    };

    for (const [subject, keywords] of Object.entries(subjectMappings)) {
      // Check if this subject exists in active subjects
      const matchingSubject = activeSubjects.find((s) => s.toLowerCase() === subject.toLowerCase());
      if (matchingSubject && keywords.some((keyword) => aiSubjectLower.includes(keyword))) {
        return matchingSubject;
      }
    }

    // If no match found, return the first active subject or 'General'
    return activeSubjects[0] || "General";
  }

  /**
   * Save AI-generated flashcards to Firebase flashcards collection
   */
  async saveFlashcards(
    flashcardResult: FlashcardResult,
    sourceFileName: string | null,
    originalContent: string,
    sourceType: "pdf" | "txt" | "docx" | "note" = "note",
    uploadedFile: File | null = null,
    sessionId?: string // ✅ NEW: Optional session ID from completed session
  ): Promise<string> {
    try {
      logInfo("Starting flashcards save process");

      // Wait for auth state to be ready if needed
      await this.waitForAuthState();

      // Get current student user information (will throw if authentication fails)
      const userInfo = await this.getCurrentStudentUser();

      logInfo("✅ User authentication validated", {
        userId: userInfo.userId.substring(0, 8) + "***",
        userName: userInfo.userName,
      });

      // Upload file to Storage if provided
      let studentNotesUrl: string | undefined;
      if (uploadedFile) {
        logInfo("File detected, uploading to Storage");
        studentNotesUrl = await this.uploadStudentNotesToStorage(uploadedFile, userInfo.userId);
        logSuccess("File uploaded successfully");
      }

      // Get active subjects for matching
      const activeSubjects = await this.getActiveSubjects();
      logDebug("Active subjects retrieved", { count: activeSubjects.length });

      // ✅ NEW: If sessionId is provided, fetch session data from Firebase
      let sessionSubject: string | null = null;
      if (sessionId) {
        logInfo("Session ID provided - fetching session data from Firebase", { sessionId });
        try {
          const sessionDoc = await getDoc(doc(db, "sessions", sessionId));
          if (sessionDoc.exists()) {
            const sessionData = sessionDoc.data();
            sessionSubject = sessionData.subject || null;
            logSuccess("Session data fetched successfully", {
              sessionId,
              sessionSubject,
              tutorId: sessionData.tutorId,
            });
          } else {
            logError("Session document not found in Firebase", { sessionId });
          }
        } catch (error) {
          logError("Error fetching session data from Firebase", error);
        }
      }

      // Find best matching subject (only if no session subject)
      const matchedSubject = sessionSubject
        ? sessionSubject
        : this.findBestMatchingSubject(flashcardResult.subject, activeSubjects);

      logDebug("Subject determination completed", {
        aiSubject: flashcardResult.subject,
        sessionSubject,
        matchedSubject,
        source: sessionSubject ? "Firebase session" : "AI detection + matching",
      });

      // Use provided session ID or generate new one
      const finalSessionId =
        sessionId || `flashcards_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      if (sessionId) {
        logInfo("Using provided session ID from completed session", { sessionId });
      }

      // Create flashcard record
      const flashcardRecord: Omit<FlashcardRecord, "flashcards_id"> = {
        session_id: finalSessionId,
        user_id: userInfo.userId,
        userName: userInfo.userName,
        flashcard_data: flashcardResult.flashcards,
        date_generated: serverTimestamp(),
        ai_model_used: "gemini-2.5-flash", // Specific model used for flashcards
        source_type: sourceType,
        input_length: originalContent.length,
        subject: matchedSubject,
        topic: flashcardResult.topic,
        sources: flashcardResult.sources || [],
        filename: sourceFileName || "", // ✅ FIX: Use empty string instead of undefined for Firebase compatibility
        student_notes: originalContent,
        student_notes_url: studentNotesUrl || "", // ✅ FIX: Use empty string instead of undefined when no file uploaded
      };

      logDebug("Flashcard record prepared", {
        sessionId: finalSessionId,
        subject: matchedSubject,
        aiGeneratedSubject: flashcardResult.subject,
        cardCount: flashcardResult.flashcards.length,
        sourceType,
        inputLength: originalContent.length,
        hasFile: !!studentNotesUrl,
        hasFilename: !!sourceFileName,
      });

      // Save to Firebase
      logInfo("📤 Saving flashcards to Firebase flashcards collection...");
      const docRef = await addDoc(this.flashcardsCollection, flashcardRecord);

      logSuccess("✅ FLASHCARDS SAVED TO FIREBASE SUCCESSFULLY", {
        documentId: docRef.id,
        collection: "flashcards",
        topic: flashcardRecord.topic,
        subject: matchedSubject,
        cardCount: flashcardResult.flashcards.length,
        sessionId: finalSessionId,
        timestamp: new Date().toISOString(),
      });

      // Additional console log for visibility
      console.log("🎉 Flashcards Document Created:", {
        id: docRef.id,
        topic: flashcardRecord.topic,
        subject: matchedSubject,
        cardCount: flashcardResult.flashcards.length,
      });

      return docRef.id;
    } catch (error) {
      logError("❌ ERROR SAVING FLASHCARDS", error);
      console.error("🚨 Flashcards save error details:", error);
      throw error;
    }
  }

  /**
   * Wait for Firebase auth state to be ready
   */
  private async waitForAuthState(): Promise<void> {
    return new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        unsubscribe();
        resolve();
      });

      // Fallback timeout to avoid hanging
      setTimeout(() => {
        unsubscribe();
        resolve();
      }, 5000);
    });
  }

  /**
   * Get flashcards for current student user
   */
  async getUserFlashcards(limit?: number): Promise<FlashcardRecord[]> {
    try {
      const userInfo = await this.getCurrentStudentUser();
      if (!userInfo) {
        throw new Error("Authentication required to get flashcards");
      }

      const flashcardsQuery = limit
        ? query(
            this.flashcardsCollection,
            where("user_id", "==", userInfo.userId),
            orderBy("date_generated", "desc")
            // Note: Firestore limit would go here if needed
          )
        : query(
            this.flashcardsCollection,
            where("user_id", "==", userInfo.userId),
            orderBy("date_generated", "desc")
          );

      const snapshot = await getDocs(flashcardsQuery);
      const flashcards: FlashcardRecord[] = [];

      snapshot.forEach((doc) => {
        flashcards.push({
          flashcards_id: doc.id,
          ...doc.data(),
        } as FlashcardRecord);
      });

      logDebug("Retrieved user flashcards", { count: flashcards.length });
      return flashcards;
    } catch (error) {
      logError("Error getting user flashcards", error);
      return [];
    }
  }

  /**
   * Set up real-time listener for user's flashcards
   */
  setupFlashcardsListener(
    userId: string,
    callback: (flashcards: FlashcardRecord[]) => void,
    errorCallback?: (error: Error) => void
  ): () => void {
    try {
      const flashcardsQuery = query(
        this.flashcardsCollection,
        where("user_id", "==", userId),
        orderBy("date_generated", "desc")
      );

      return onSnapshot(
        flashcardsQuery,
        (snapshot) => {
          const flashcards: FlashcardRecord[] = [];
          snapshot.forEach((doc) => {
            flashcards.push({
              flashcards_id: doc.id,
              ...doc.data(),
            } as FlashcardRecord);
          });
          callback(flashcards);
        },
        (error) => {
          logError("Error in flashcards listener", error);
          if (errorCallback) {
            errorCallback(error);
          }
        }
      );
    } catch (error) {
      logError("Error setting up flashcards listener", error);
      if (errorCallback) {
        errorCallback(error as Error);
      }
      return () => {}; // Return empty unsubscribe function
    }
  }
}

// Lazy singleton instance to avoid circular dependency issues
let flashcardServiceInstance: FlashcardService | null = null;

export function getFlashcardService(): FlashcardService {
  if (!flashcardServiceInstance) {
    flashcardServiceInstance = new FlashcardService();
  }
  return flashcardServiceInstance;
}

// For backward compatibility, export a proxy that lazily initializes
export const flashcardService = new Proxy({} as FlashcardService, {
  get(target, prop) {
    return getFlashcardService()[prop as keyof FlashcardService];
  },
});
export default FlashcardService;
